/**
 * Meeting Detail → Recap (#3182): the wire shapes of `meeting-recaps`
 * (packages/api/src/services/read-ai/meeting-recaps/meeting-recaps.ts) and the
 * sentences the tab says about them. Pure, so every word a person reads about a
 * send is pinned by meetingRecap.types.test.ts.
 *
 * THE RULE FOR EVERY SENTENCE HERE: say what HQ knows. SendGrid answering 202
 * means it took the message, never that anyone received it; a sandboxed send
 * reached nobody; a transport error may or may not have gone.
 */

export type RecapAudience = "internal" | "client";

export interface RecapRecipient {
  email: string;
  name: string;
  attended: boolean;
  suggested: boolean;
  title?: string | null;
  isPrimary?: boolean;
}

export interface RecapSend {
  id: number;
  audience: RecapAudience;
  to: string[];
  subject: string;
  /** accepted | refused | error — SendGrid's answer, never "delivered". */
  providerStatus: string;
  sandbox: boolean;
  problem: string | null;
  sentBy: string | null;
  createdAt: string | null;
}

export interface RecapDraft {
  /** `<meetingId>:<audience>`: one cache entry per audience. */
  id: string;
  meetingId: number;
  audience: RecapAudience;
  meetingTitle: string | null;
  clientId: string | null;
  clientName: string | null;
  subject: string;
  text: string;
  recipients: RecapRecipient[];
  blocked: string | null;
  otherClients: string[];
  items: { included: number; closedOrRejected: number; otherClient: number };
  email: { configured: boolean; sandbox: boolean };
  sends: RecapSend[];
}

export interface RecapSendResult {
  meetingId: number;
  audience: RecapAudience;
  documentId: string;
  emailSendId: number | null;
  providerStatus: "accepted" | "refused" | "error";
  accepted: boolean;
  sandbox: boolean;
  problem: string | null;
  addressed: number;
  acceptedFor: number;
}

export type Severity = "success" | "info" | "warning" | "error";

const people = (n: number) => `${n} ${n === 1 ? "person" : "people"}`;

/**
 * What one send's answer means, in words. `what` names the message ("the
 * recap", "the email"): the client-question email says the same things the
 * same way (InquiryQueue/inquiryDelivery.types.ts).
 */
export function sendOutcomeWording(
  r: Pick<RecapSendResult, "accepted" | "sandbox" | "acceptedFor" | "providerStatus" | "problem">,
  what = "the recap",
): {
  severity: Severity;
  text: string;
} {
  if (r.accepted && r.sandbox)
    return {
      severity: "warning",
      text: `Not delivered: this HQ is in email test mode, so SendGrid checked ${what} for ${people(r.acceptedFor)} and delivered it to nobody.`,
    };
  if (r.accepted)
    return {
      severity: "success",
      text: `SendGrid accepted ${what} for ${people(r.acceptedFor)}. Accepted means SendGrid took it; HQ is not told whether it reached their inbox.`,
    };
  if (r.providerStatus === "error")
    return {
      severity: "error",
      text: `HQ could not reach SendGrid${r.problem ? ` (${r.problem})` : ""}. It may or may not have gone: check Admin → Email before sending it again.`,
    };
  return {
    severity: "error",
    text: `Not sent: ${r.problem ?? "SendGrid refused it."}`,
  };
}

/** One line of a "sent" list: the meeting's recaps, or a client question's emails. */
export function sendStatusLabel(s: Pick<RecapSend, "providerStatus" | "sandbox">): {
  label: string;
  color: "success" | "warning" | "error";
} {
  if (s.providerStatus === "accepted")
    return s.sandbox
      ? { label: "Test mode: not delivered", color: "warning" }
      : { label: "Accepted by SendGrid", color: "success" };
  if (s.providerStatus === "error")
    return { label: "Unknown: HQ lost SendGrid's answer", color: "warning" };
  return { label: "Not sent", color: "error" };
}

/** Why an item count differs from the meeting's, said once under the draft. */
export function itemsWording(d: RecapDraft): string {
  const parts = [
    `${d.items.included} action item${d.items.included === 1 ? " is" : "s are"} in the draft.`,
  ];
  if (d.items.closedOrRejected)
    parts.push(
      `${d.items.closedOrRejected} closed or rejected ${d.items.closedOrRejected === 1 ? "is" : "are"} left out.`,
    );
  if (d.items.otherClient)
    parts.push(
      `${d.items.otherClient} that ${d.items.otherClient === 1 ? "belongs" : "belong"} to another client ${d.items.otherClient === 1 ? "is" : "are"} left out.`,
    );
  return parts.join(" ");
}
